import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { api, errMsg, imgSrc } from "../../lib/api";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

const EMPTY = { title: "", subtitle: "", description: "", badge: "KEYS", price: "", stock: "", min_qty: "1", image: "" };

export const ProductForm = ({ product, onClose, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...product });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { title: f.title, subtitle: f.subtitle, description: f.description, badge: f.badge, price: Number(f.price), stock: parseInt(f.stock, 10), min_qty: parseInt(f.min_qty, 10) || 1, image: f.image };
    try {
      if (product.id) await api.put(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success("Product saved");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image is too large (max 5 MB)");
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image: r.data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const field = (k, ph, props = {}) => <input data-testid={`product-form-${k}`} value={f[k]} onChange={set(k)} placeholder={ph} className="field" {...props} />;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#161230] text-white" data-testid="product-form-dialog">
        <DialogTitle className="font-display">{product.id ? "Edit product" : "New product"}</DialogTitle>
        <form onSubmit={save} className="space-y-3">
          {field("title", "Title", { required: true })}
          {field("subtitle", "Short line under the title")}
          <textarea data-testid="product-form-description" value={f.description} onChange={set("description")} placeholder="Description" rows={3} className="field" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {field("badge", "Badge")}
            {field("price", "Price USD", { required: true, type: "number", step: "0.01", min: "0.01" })}
            {field("stock", "Stock", { required: true, type: "number", min: "0" })}
            {field("min_qty", "Min qty", { type: "number", min: "1" })}
          </div>
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="product-form-image-picker"
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 py-4 text-sm text-zinc-300 transition-colors hover:border-violet-500/60 hover:text-white">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {uploading ? "Uploading..." : "Upload image from device"}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden onChange={pick} data-testid="product-form-image-input" />
          {field("image", "Image URL (optional)")}
          {f.image && <img src={imgSrc(f.image)} alt="" className="aspect-[16/10] w-full rounded-xl object-cover" data-testid="product-form-image-preview" />}
          <button data-testid="admin-save-product-button" disabled={busy} className="btn-primary w-full">{busy ? "Saving..." : "Save"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
